import React, { useCallback, useEffect, useRef } from "react";
import { useI18n, useNationalityFormatter } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { Document, DocumentType, Gender, MaritalStatus } from "../../__generated__/graphql";
import DateField from "../../components/DateField";
import SelectField from "../../components/SelectField";
import { StyleSheet, TextInput } from "react-native";
import { BodyMediumText, BodySmallText } from "../../components/FormattedText";
import OptionsField from "../../components/OptionsField";
import Gap from "../../ui_atoms/Gap";
import MarkdownView from "../../components/MarkdownView";
import { useIntercom } from "../../hooks/intercom";
import { useTheme } from "../../hooks/use-theme";
import ListItem from "../../ui_atoms/ListItem";
import Loading from "../../components/Loading";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import DocumentEditor from "../../components/document/DocumentEditor";

const WorkerProfileEditorScreen = () => {
    const { user } = React.useContext(AuthManagerContext);
    const { colors, marginSize } = useTheme();
    const { presentMessageComposer } = useIntercom();
    const [displayedDocument, setDisplayedDocument] = React.useState<Document | undefined>(
        undefined,
    );
    const i18n = useI18n();
    const { isFiscalAttestationRequired } = usePostOnboardingData();
    const addressRef = useRef<TextInput>(null);

    useEffect(() => {
        setTimeout(() => {
            addressRef.current?.setSelection(0, 0);
            addressRef.current?.blur();
        }, 0);
    }, []);

    const formatNationality = useNationalityFormatter();

    const onValidate = useCallback(async () => {
        await presentMessageComposer();
    }, [presentMessageComposer]);

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    const isMarried = user.workerProfile?.maritalStatus === MaritalStatus.Married;

    const hasFiscalAttestation = !!user.workerProfile?.hasFiscalAttestation;
    const partner = user.workerProfile?.partner;

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("profile.contact_support"),
                    onPress: onValidate,
                    mode: "secondary",
                },
            ]}
        >
            <DocumentEditor
                visible={!!displayedDocument}
                document={displayedDocument}
                onClose={() => setDisplayedDocument(undefined)}
            />
            <VStack gap={1} alignment="stretch" h="2/3">
                {user.gender && (
                    <TextField
                        label={i18n.t("user.gender")}
                        value={i18n.t(`civility.${user.gender}`)}
                        disabled
                    />
                )}
                {user.firstName && (
                    <TextField value={user.firstName} label={i18n.t("user.first_name")} disabled />
                )}
                {user.lastName && (
                    <TextField value={user.lastName} label={i18n.t("user.last_name")} disabled />
                )}
                {user.birthdate && (
                    <DateField
                        value={user.birthdate ? new Date(user.birthdate) : undefined}
                        label={i18n.t("user.birthdate")}
                        disabled
                    />
                )}
                <TextField
                    ref={addressRef}
                    value={user.address?.formatted}
                    right="map-marker"
                    label={i18n.t("user.residential_address")}
                    disabled
                />
                {user.nationality && user.gender && (
                    <TextField
                        value={formatNationality(user.nationality, user.gender, "guess")}
                        label={i18n.t("profile.nationality")}
                        disabled
                    />
                )}
                {user.workPermit && (
                    <TextField
                        value={i18n.t(`work_permit.${user.workPermit}`)}
                        label={i18n.t("profile.work_permit_label")}
                        disabled
                    />
                )}
                <TextField
                    value={user.workerProfile?.avsNumber || ""}
                    label={i18n.t("profile.avs_number")}
                    disabled
                />
                <>
                    <TextField
                        isSelfContained={!!partner}
                        value={`${i18n.t(`user.${user.workerProfile?.maritalStatus}${isMarried ? "_with_x_revenues" : ""}`, { count: user.workerProfile?.partner?.monthlySalary ? 2 : 1 })}`}
                        label={i18n.t("user.marital_status")}
                        disabled
                    />
                    {partner && (
                        <TextField
                            isSelfContained
                            style={styles.stackedField}
                            value={user.workerProfile?.partner?.firstName || ""}
                            label={i18n.t("screens.profile_editor.civil_state.partner_first_name")}
                            disabled
                        />
                    )}
                    {partner && (
                        <TextField
                            isSelfContained
                            style={styles.stackedField}
                            value={user.workerProfile?.partner?.lastName || ""}
                            label={i18n.t("screens.profile_editor.civil_state.partner_last_name")}
                            disabled
                        />
                    )}
                    {partner && (
                        <TextField
                            isSelfContained
                            value={user.workerProfile?.partner?.avsNumber || ""}
                            style={
                                user.workerProfile?.partner?.monthlySalary
                                    ? styles.stackedField
                                    : {
                                          borderTopRightRadius: 0,
                                          borderTopLeftRadius: 0,
                                          marginTop: -2,
                                      }
                            }
                            label={i18n.t("screens.profile_editor.civil_state.partner_avs")}
                            disabled
                        />
                    )}
                    {isMarried &&
                        typeof user.workerProfile?.partner?.monthlySalary === "number" && (
                            <TextField
                                isSelfContained
                                value={(user.workerProfile.partner.monthlySalary / 100).toString()}
                                style={{
                                    borderTopRightRadius: 0,
                                    borderTopLeftRadius: 0,
                                    marginTop: -2,
                                }}
                                right="CHF"
                                label={i18n.t("screens.profile_editor.civil_state.partner_revenue")}
                                disabled
                            />
                        )}
                </>
                <Gap gap={0.25} />
                {user.workerProfile?.hasChildren != null && (
                    <>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.children.do_you_have_children")}
                        </BodyMediumText>
                        <OptionsField
                            value={user.workerProfile?.hasChildren}
                            data={[
                                { label: i18n.t("yes"), value: true },
                                { label: i18n.t("no"), value: false },
                            ]}
                            onSelect={() => {}}
                            disabled
                            shrinked
                        />
                        <Gap gap={0.25} />
                        <VStack gap={0.25}>
                            {user.workerProfile?.children?.map((children, index) => {
                                const childNb = index + 1;

                                return (
                                    <React.Fragment key={index}>
                                        <TextField
                                            style={{
                                                borderBottomRightRadius: 0,
                                                borderBottomLeftRadius: 0,
                                            }}
                                            value={children.firstName || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_first_name",
                                            )} ${childNb}`}
                                            disabled
                                        />
                                        <TextField
                                            isSelfContained
                                            style={styles.stackedField}
                                            value={children.lastName || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_last_name",
                                            )} ${childNb}`}
                                            disabled
                                        />
                                        <DateField
                                            isSelfContained
                                            style={styles.stackedField}
                                            value={
                                                children.birthdate
                                                    ? new Date(children.birthdate)
                                                    : undefined
                                            }
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_birth_date",
                                            )} ${childNb}`}
                                            disabled
                                        />
                                        <SelectField<Gender>
                                            isSelfContained
                                            value={children.gender || Gender.Unknown}
                                            style={styles.stackedField}
                                            data={[Gender.Male, Gender.Female].map((v) => ({
                                                value: v,
                                                label: i18n.t(`user.gender_kid_${v}`),
                                            }))}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_gender",
                                            )} ${childNb}`}
                                            disabled
                                        />
                                        <TextField
                                            isSelfContained
                                            style={{
                                                borderTopRightRadius: 0,
                                                borderTopLeftRadius: 0,
                                                marginTop: -2,
                                            }}
                                            value={children.avsNumber || ""}
                                            label={`${i18n.t(
                                                "screens.profile_editor.children.children_avs",
                                            )} ${childNb}`}
                                            disabled
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </VStack>
                    </>
                )}
                <Gap gap={0.25} />
                {user.workerProfile?.hasOtherActivity != null && (
                    <>
                        <BodyMediumText>
                            {i18n.t("screens.profile_editor.civil_state.has_other_activity")}
                        </BodyMediumText>
                        <OptionsField
                            value={user.workerProfile.hasOtherActivity}
                            data={[
                                { label: i18n.t("yes"), value: true },
                                { label: i18n.t("no"), value: false },
                            ]}
                            disabled
                            shrinked
                        />
                    </>
                )}
                <Gap gap={0.25} />
                {isFiscalAttestationRequired && hasFiscalAttestation != null && (
                    <VStack gap={0.5}>
                        <>
                            <BodyMediumText>
                                {i18n.t(
                                    "screens.profile_editor.fiscal_attestation.has_fiscal_attestation",
                                )}
                            </BodyMediumText>
                            <OptionsField
                                value={hasFiscalAttestation}
                                data={[
                                    { label: i18n.t("yes"), value: true },
                                    { label: i18n.t("no"), value: false },
                                ]}
                                disabled
                                shrinked
                            />
                        </>
                        <ListItem
                            description={i18n.t("screens.profile_editor.fiscal_attestation.title")}
                            style={{ marginHorizontal: -marginSize }}
                            onPress={() => {
                                const document = user.workerProfile?.documents?.find(
                                    (d) => d.type === DocumentType.FiscalAttestation,
                                );
                                setDisplayedDocument(document);
                            }}
                        />
                        <Gap gap={0.5} />
                        <BodySmallText color={colors.fieldLabel}>
                            {hasFiscalAttestation
                                ? i18n.t(
                                      "screens.profile_editor.fiscal_attestation.has_fiscal_attestation_description",
                                  )
                                : i18n.t(
                                      "screens.profile_editor.fiscal_attestation.has_no_fiscal_attestation_description",
                                  )}
                        </BodySmallText>
                    </VStack>
                )}
                <Gap gap={0.5} />
                <MarkdownView
                    markdown={i18n.t("profile.contact_us_for_change")}
                    onLinkPress={() => {
                        void presentMessageComposer();
                        return false;
                    }}
                />
            </VStack>
        </ScreenCard>
    );
};

const styles = StyleSheet.create({
    stackedField: {
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        marginTop: -2,
    },
    link: {
        textDecorationLine: "underline",
    },
});

export default WorkerProfileEditorScreen;
